import React, { KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import AppIcon, { AppIconType } from "../AppIcon/AppIcon";
import AppText, { FormLabel } from "../AppText/AppText";
import LoadingIndicator from "../../Shared/LoadingIndicator/LoadingIndicator";
import { appTheme } from "../../../theme/Theme";

export interface AppInputNewProps {
	type?: string;
	value?: string | number;
	onChange?: any;
	onBlurText?: () => void;
	onChangeText?: (text: string) => void;
	onChangeNumber?: (val: number, e: React.FormEvent<HTMLInputElement>) => void;
	name?: string;
	errors?: any;
	label?: string | string[];
	icon?: AppIconType;
	loading?: boolean;
	disabled?: boolean;
	placeholder?: string;
	className?: string;
	eventOnKeyDown?: any;
	field?: string;
	containerRef?: any;
	onFocus?: any;
	pattern?: any;
	/**
	 * Min value, only applicable if type
	 * is number and onChangeNumber is used
	 */
	min?: string | number;
	/**
	 * Max value, only applicable if type
	 * is number and onChangeNumber is used
	 */
	max?: string | number;
	step?: string;
	/** Max number of characters (native `maxlength`). */
	maxLength?: number;
	readOnly?: boolean;
	defaultValue?: string;
	description?: string;
	/**
	 * Will shows asterisk next to label which indicates that field
	 * is mandatory. Will not add any validation logic!
	 */
	mandatory?: boolean;

	/**
	 * If text in input should be selected when input is focused.
	 *
	 * This will from user perspective make it slightly easier to
	 * replace the value if input if of such kind where it makes sense.
	 */
	selectOnFocus?: boolean;

	/**
	 * Convenient callback to handle when user presses Enter
	 * on keydown.
	 */
	onEnter?: (e: KeyboardEvent) => void;

	onClick?: (e: any) => void;

	/**
	 * Optional tooltip ℹ️
	 */
	tooltip?: string | React.ReactNode;

	/**
	 * Optional knowledge base tag to open knowledge base sidebar
	 */
	knowledgeBaseTag?: string;

	/**
	 * Optional custom tooltip text for knowledge base trigger
	 */
	knowledgeBaseTooltip?: string;

	/**
	 * Optional knowledge base tag that only shows on hover (reduces visual clutter)
	 */
	showKnowledgeBaseForTagOnHover?: string;

	autoComplete?: string;

	/**
	 * If top skip attempt to translate label.
	 * Should in most cases not be needed.
	 */
	skipLabelTranslation?: boolean;

	textAlign?: "center" | "left" | "right";

	prefix?: string;

	isValid?: boolean;

	/**
	 * Custom className for the input container
	 */
	containerClassName?: string;

	/**
	 * Custom className for the input wrapper (the styled border container)
	 */
	wrapperClassName?: string;

	/**
	 * Custom className for the input element itself
	 */
	inputClassName?: string;
}

const AppInputNew = React.forwardRef<HTMLInputElement, AppInputNewProps>(
	(
		{
			name,
			type,
			value,
			onChange,
			onBlurText,
			onChangeText,
			onChangeNumber,
			errors,
			label,
			icon,
			disabled,
			placeholder,
			className,
			eventOnKeyDown,
			field,
			containerRef,
			onFocus,
			pattern,
			min,
			max,
			step,
			maxLength,
			readOnly,
			defaultValue,
			description,
			mandatory,
			selectOnFocus,
			onEnter,
			tooltip,
			knowledgeBaseTag,
			knowledgeBaseTooltip,
			showKnowledgeBaseForTagOnHover,
			autoComplete = "off",
			skipLabelTranslation = false,
			loading,
			textAlign,
			prefix,
			onClick,
			isValid = true,
			containerClassName,
			wrapperClassName,
			inputClassName,
		},
		ref
	) => {
		const { t } = useTranslation();
		const innerInputRef = useRef<HTMLInputElement>(null);
		const theRef = ref !== undefined ? ref : innerInputRef;

		function handleOnFocus(event: React.FocusEvent<HTMLInputElement>) {
			// Auto-select for number inputs with existing values, or when selectOnFocus is explicitly set
			const shouldSelect =
				selectOnFocus ||
				(type === "number" && value !== undefined && value !== null && value !== "");

			if (shouldSelect) {
				// Use setTimeout to ensure selection happens after focus is complete
				setTimeout(() => {
					event.target.select();
				}, 0);
			}
			onFocus && onFocus(event);
		}

		function handleOnClick(event: React.MouseEvent<HTMLInputElement>) {
			// Auto-select for number inputs with existing values, or when selectOnFocus is explicitly set
			const shouldSelect =
				selectOnFocus ||
				(type === "number" && value !== undefined && value !== null && value !== "");

			if (shouldSelect) {
				// Use setTimeout to ensure selection happens after click positioning is complete
				setTimeout(() => {
					// Null check to prevent errors if element is unmounted
					if (event.currentTarget) {
						event.currentTarget.select();
					}
				}, 0);
			}
		}

		function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
			if (e.key === "Enter") {
				onEnter && onEnter(e);
			}

			eventOnKeyDown && eventOnKeyDown(e);
		}

		// Base classes for different parts
		const containerClasses = `w-full ${containerClassName || ""}`;

		const wrapperClasses = `
			flex max-w-full p-2 rounded-sm bg-white h-[2.3125rem] border border-gray-300
			focus-within:border-blue-900 focus-within:bg-white
			${disabled ? "!bg-gray-100 cursor-not-allowed" : ""}
			${!isValid || errors ? "!border-red-500 !bg-red-50" : ""}
			${wrapperClassName || ""}
		`
			.trim()
			.replace(/\s+/g, " ");

		const inputClasses = `
			border-none w-full text-[0.9375rem] leading-[0.9375rem] bg-transparent outline-none
			${textAlign === "center" ? "text-center" : ""}
			${textAlign === "right" ? "text-right" : ""}
			${textAlign === "left" ? "text-left" : ""}
			${disabled ? "text-gray-400 cursor-not-allowed" : ""}
			${inputClassName || ""}
		`
			.trim()
			.replace(/\s+/g, " ");

		return (
			<div className={containerClasses} ref={containerRef} onClick={onClick}>
				{label && (
					<FormLabel
						tooltip={tooltip}
						knowledgeBaseTag={knowledgeBaseTag}
						knowledgeBaseTooltip={knowledgeBaseTooltip}
						showKnowledgeBaseForTagOnHover={showKnowledgeBaseForTagOnHover}
					>
						{skipLabelTranslation ? label : t(label as any)}
						{mandatory ? " *" : ""}
					</FormLabel>
				)}
				<div className={wrapperClasses}>
					{loading && (
						<div className="max-w-[20px] mr-[5px]">
							<LoadingIndicator size="s" showText={false} />
						</div>
					)}

					{!prefix && icon && !loading && (
						<AppIcon icon={icon} color={"#999"} margin="0 7px 0 1px" />
					)}

					{prefix && (
						<div className="flex bg-[#DBDBDB] w-fit py-[2px] px-1 rounded-sm mr-[6px] focus-within:bg-[#D3EBF6]">
							{icon && (
								<AppIcon
									icon={icon}
									size={"20px"}
									color={appTheme.colors.COLOR_GRAY_TEXT}
									margin="0 2px 0 3px"
								/>
							)}

							<div className="text-xs text-gray-600 font-semibold">{prefix}</div>
						</div>
					)}

					<input
						className={inputClasses}
						pattern={pattern}
						min={min}
						max={max}
						step={step}
						maxLength={maxLength}
						onFocus={handleOnFocus}
						onClick={handleOnClick}
						ref={theRef}
						name={name}
						onChange={(e) => {
							if (onChange) onChange(e);
							if (onChangeText) onChangeText(e.target.value);
							if (onChangeNumber) {
								let { value, min, max } = e.target;
								let validatedValue = Number(value);

								if (min !== undefined && max !== "") {
									validatedValue = Math.max(validatedValue, Number(min));
								}
								if (max !== undefined && max !== "") {
									validatedValue = Math.min(validatedValue, Number(max));
								}

								onChangeNumber(validatedValue, e);
							}
						}}
						onBlur={onBlurText}
						value={value}
						type={type}
						disabled={disabled}
						placeholder={placeholder && t(placeholder as any)}
						onKeyDown={handleKeyDown}
						defaultValue={defaultValue}
						readOnly={readOnly}
						autoComplete={autoComplete}
					/>
				</div>
				{description && (
					<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="grayText">
						{description}
					</AppText>
				)}
				<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="danger">
					{errors && t(errors as any)}
				</AppText>
			</div>
		);
	}
);

AppInputNew.displayName = "AppInputNew";

export default AppInputNew;
